import { api } from '@/lib/api'
import type {
  AccountMapping,
  BalanceSheetResponse,
  CashFlowResponse,
  CFMapping,
  DrillDownResponse,
  IncomeStatementResponse,
  PostingStatusResponse,
  PostTransactionsResponse,
  ReportSnapshotDetail,
  ReportSnapshotSummary,
  ReportingPeriod,
  TrialBalanceResponse,
  UnpostedPreviewResponse,
} from './types'

const base = '/api/reporting'

export const reportingApi = {
  postingStatus: () => api.get<PostingStatusResponse>(`${base}/posting-status`),
  unpostedPreview: () => api.get<UnpostedPreviewResponse>(`${base}/unposted-preview`),
  postTransactions: (fiscalStartMonth: number) =>
    api.post<PostTransactionsResponse>(`${base}/post-transactions`, { fiscalStartMonth }),

  balanceSheet: (asOfDates: string[], fiscalStartMonth: number, bankAccountIds?: number[]) =>
    api.post<BalanceSheetResponse>(`${base}/balance-sheet`, {
      asOfDates,
      fiscalStartMonth,
      bankAccountIds: bankAccountIds && bankAccountIds.length > 0 ? bankAccountIds : undefined,
    }),
  incomeStatement: (body: {
    startDate: string
    endDate: string
    granularity: 'total' | 'monthly' | 'quarterly'
    fiscalStartMonth: number
    bankAccountIds?: number[]
  }) => api.post<IncomeStatementResponse>(`${base}/income-statement`, body),
  cashFlow: (body: { startDate: string; endDate: string; fiscalStartMonth: number; bankAccountIds?: number[] }) =>
    api.post<CashFlowResponse>(`${base}/cash-flow`, body),
  trialBalance: (asOfDate: string, bankAccountIds?: number[]) =>
    api.post<TrialBalanceResponse>(`${base}/trial-balance`, {
      asOfDate,
      bankAccountIds: bankAccountIds && bankAccountIds.length > 0 ? bankAccountIds : undefined,
    }),

  drillDown: (body: {
    mainCategory: string
    subcategory: string
    endDate: string
    startDate?: string
  }) => api.post<DrillDownResponse>(`${base}/drill-down`, body),

  // CF mappings
  cfMappings: () => api.get<{ mappings: CFMapping[] }>(`${base}/cf-mappings`),
  putCfMappings: (mappings: CFMapping[]) =>
    api.put(`${base}/cf-mappings`, { mappings }),
  deleteCfMapping: (accountCode: string) =>
    api.del(`${base}/cf-mappings/${encodeURIComponent(accountCode)}`),

  // Periods
  periods: (year: number) =>
    api.get<{ periods: ReportingPeriod[] }>(`${base}/periods/${year}`),
  generatePeriods: (year: number, fiscalStartMonth: number) =>
    api.post<{ periods: ReportingPeriod[] }>(`${base}/periods/${year}/generate`, {
      fiscalStartMonth,
    }),
  closePeriod: (id: number) => api.put(`${base}/periods/${id}/close`, {}),
  reopenPeriod: (id: number) => api.put(`${base}/periods/${id}/reopen`, {}),

  // Account mappings
  accountMappings: () =>
    api.get<{ mappings: AccountMapping[] }>(`${base}/account-mappings`),
  updateAccountMapping: (
    id: number,
    body: { mapped_account_code: string; mapping_status: string },
  ) => api.put(`${base}/account-mappings/${id}`, body),

  // Snapshots
  snapshots: () =>
    api.get<{ snapshots: ReportSnapshotSummary[] }>(`${base}/snapshots`),
  snapshot: (id: number) => api.get<ReportSnapshotDetail>(`${base}/snapshots/${id}`),
  saveSnapshot: (body: {
    reportType: string
    periodStart: string | null
    periodEnd: string
    fiscalYear: number
    reportData: unknown
  }) => api.post<ReportSnapshotSummary>(`${base}/snapshots`, body),
  approveSnapshot: (id: number) =>
    api.put(`${base}/snapshots/${id}/approve`, {}),
  generateMonthlySnapshots: () =>
    api.post(`${base}/admin/generate-monthly-snapshots`, {}),
}
